import React, {
  Dispatch,
  SetStateAction,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { gql, useMutation } from '@apollo/client';
import Split from 'react-split';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import { DocumentViewerProvider } from '../../contexts/DocumentViewerProvider';
import { BlockResolutions, Resolutions, ResolutionState } from './types';
import { UnresolvedDecisionsStatus } from './UnresolvedDecisionsStatus/UnresolvedDecisionsStatus';
import styles from './Compare.module.css';
import ToolbarNav from './ToolbarNav';
import UnsavedChangesWarning from './UnsavedChangesWarning';
import ConsensusTabBadge from './ConsensusTabBadge';
import { captureApolloError } from 'util/apolloError';
import { errorMessagesFrom } from 'util/errorMessages';

import {
  BlockRecords,
  normalizeBlockRecordsForWrite,
} from 'concepts/Extraction/BlocksData';
import { TemplateModel } from 'concepts/Extraction/TemplateModel';
import { prepareViewModel } from 'concepts/Extraction/ViewModel/comparison';
import Errors from 'components/Errors';
import {
  SaveConsensusMutation,
  CompleteConsensusMutation,
} from 'types/graphql';
import { ExtractionTabs } from 'concepts/Extraction/Widgets/ExtractionTabs';
import { ExtractionFormType } from 'concepts/Extraction/ExtractionFormType';
import { CompareDataExtractionForm } from 'concepts/Extraction/Compare/CompareDataExtractionForm';
import { CompareQualityAssessmentForm } from 'concepts/Extraction/Compare/CompareQualityAssessmentForm';
import { FullTextsPreview } from 'concepts/Extraction/Widgets/FullText/FullTextsPreview';
import { UnresolvedTableCellIDs } from 'concepts/Extraction/ViewModel/tables';
import { mutationFailed } from 'util/apolloMutations';
import { Button } from 'components/core';
import { Inline } from 'components/layout';
import { Document } from 'types';
import { QueryProvider } from 'query/QueryProvider';

export const SAVE_CONSENSUS_MUTATION = gql`
  mutation SaveConsensus($input: SaveConsensusInput!) {
    saveConsensus(input: $input) {
      success
      errors {
        message
      }
    }
  }
`;

export const COMPLETE_CONSENSUS_MUTATION = gql`
  mutation CompleteConsensus($input: CompleteConsensusInput!) {
    completeConsensus(input: $input) {
      success
      errors {
        message
      }
    }
  }
`;
// Deal with unresolved values here
export type Review = {
  id: string;
  name: string;
  currentExtraction: {
    revisionID: string;
    values: BlockRecords;
  };
};

export type CompareFormProps = {
  template: TemplateModel;
  reviewers: Review[];
  consensusDecider: {
    name: string;
    status: 'draft' | 'complete';
    data: {
      values: BlockRecords;
    };
  } | null;
};

const emptyComparisonResult: Resolutions = {
  resolvedData: {},
  unresolvedIDs: new Set<string>(),
  unresolvedTableCells: {},
  blockResolutions: {},
};

export type FormValues = {
  resolvedData: BlockRecords;
  unresolvedIDs: Set<string>;
  blockResolutions: BlockResolutions;
  unresolvedTableCells: { string: UnresolvedTableCellIDs };
};

type CompareHeaderProps = {
  studyId: string;
  dataExtractionForm: CompareFormProps | undefined;
  dataExtractionConsensusForm: UseFormReturn<FormValues>;
  qualityAssessmentForm: CompareFormProps | undefined;
  qualityAssessmentConsensusForm: UseFormReturn<FormValues>;
  setNavigateToPreviousPage: Dispatch<SetStateAction<boolean>>;
  completed: boolean;
  editUrl: string;
  currentUserIsTheDecider: boolean;
  backUrl: string;
  title: string;
};

const CompareHeader = ({
  studyId,
  dataExtractionForm,
  dataExtractionConsensusForm,
  qualityAssessmentForm,
  qualityAssessmentConsensusForm,
  setNavigateToPreviousPage,
  currentUserIsTheDecider,
  editUrl,
  completed,
  backUrl,
  title,
}: CompareHeaderProps) => {
  const [
    saveMutation,
    {
      data: saveData,
      error: saveError,
      loading: saving,
      reset: resetSaveMutation,
    },
  ] = useMutation<SaveConsensusMutation>(SAVE_CONSENSUS_MUTATION);
  if (saveError) captureApolloError(saveError);

  const [
    completeMutation,
    {
      data: completeData,
      error: completeError,
      loading: completing,
      reset: resetCompleteMutation,
    },
  ] = useMutation<CompleteConsensusMutation>(COMPLETE_CONSENSUS_MUTATION);
  if (completeError) captureApolloError(completeError);

  const [unexpectedError, setUnexpectedError] = useState<string | null>(null);

  function save(options: { complete: boolean }) {
    setUnexpectedError(null);

    let dataExtraction;
    let qualityAssessment;

    try {
      dataExtraction = dataExtractionForm
        ? {
            sourceIds: dataExtractionForm.reviewers.map(
              (reviewer) => reviewer.currentExtraction.revisionID
            ),
            data: {
              values: normalizeBlockRecordsForWrite(
                dataExtractionConsensusForm.getValues('resolvedData')
              ),
            },
            unresolvedFields: unresolvedFields(
              dataExtractionForm.reviewers,
              dataExtractionConsensusForm.getValues('blockResolutions')
            ),
          }
        : undefined;

      qualityAssessment = qualityAssessmentForm
        ? {
            sourceIds: qualityAssessmentForm.reviewers.map(
              (reviewer) => reviewer.currentExtraction.revisionID
            ),
            data: {
              values: normalizeBlockRecordsForWrite(
                qualityAssessmentConsensusForm.getValues('resolvedData')
              ),
            },
            unresolvedFields: unresolvedFields(
              qualityAssessmentForm.reviewers,
              qualityAssessmentConsensusForm.getValues('blockResolutions')
            ),
          }
        : undefined;
    } catch (e) {
      MetaError.notify(e);
      setUnexpectedError(
        'An unexpected error occurred. Please try reloading the page.'
      );
      return;
    }

    const input = {
      studyId: studyId,
      dataExtraction,
      qualityAssessment,
    };

    type Mutation = (options: {
      variables: { input: typeof input };
    }) => Promise<unknown>;

    // Reset the state of the previous mutations to clear error messages
    resetSaveMutation();
    resetCompleteMutation();

    const mutation: Mutation = options.complete
      ? completeMutation
      : saveMutation;

    mutation({ variables: { input } })
      .then((response: any) => {
        if (mutationFailed(response)) {
          return;
        }
        // Reset form after submission to reset state values, eg: isDirty
        // Allows user to leave the page without getting unsaved changes prompt
        if (dataExtractionForm) {
          dataExtractionConsensusForm.reset({}, { keepValues: true });
        }
        if (qualityAssessmentForm) {
          qualityAssessmentConsensusForm.reset({}, { keepValues: true });
        }

        if (options.complete) {
          setNavigateToPreviousPage(true);
        }
      })
      .catch((e) => {
        MetaError.notify(e);
      });
  }

  let buttons: JSX.Element = <></>;
  if (completed && currentUserIsTheDecider) {
    buttons = (
      <Button to={editUrl} type="brand">
        Edit
      </Button>
    );
  } else if (!completed) {
    buttons = (
      <Inline spacing={4}>
        <Button
          type="brand"
          variant="outline"
          disabled={saving}
          onClick={() => {
            save({
              complete: false,
            });
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button
          type="brand"
          disabled={completing}
          onClick={() => {
            save({
              complete: true,
            });
          }}
        >
          {completing ? 'Completing…' : 'Complete'}
        </Button>
      </Inline>
    );
  }

  return (
    <header className={styles.header}>
      <ToolbarNav backUrl={backUrl} title={title} right={buttons} />
      <Errors
        title="Error saving"
        errors={errorMessagesFrom(
          unexpectedError,
          saveData?.saveConsensus?.errors,
          saveError,
          completeData?.completeConsensus?.errors,
          completeError
        )}
      />
    </header>
  );
};

export type CompareProps = {
  title: string;
  dataExtractionForm?: CompareFormProps;
  qualityAssessmentForm?: CompareFormProps;
  initialSection?: ExtractionFormType;
  singleReviewer: boolean;
  studyId: string;
  fullTexts: Document[];
  backUrl: string;
  editUrl: string;
  currentUserIsTheDecider: boolean;
  nutrientViewerKey: string;
};
type strArr = string[];

type userChangedValues = {
  reviewerId: string;
  changedFields: strArr;
};

function unresolvedFields(
  reviewers: Review[],
  blockResolutions: BlockResolutions
): userChangedValues[] {
  const reviewerResolved = Object.keys(blockResolutions).reduce(
    (acc: string[], key: string) => {
      const resolved =
        (blockResolutions[key] as ResolutionState) ===
        'resolvedByConsensusReviewer';
      return resolved ? [...acc, key] : acc;
    },
    []
  );

  return reviewers.map((reviewer) => {
    const values = reviewer.currentExtraction.values;
    const changed = Object.keys(reviewer.currentExtraction.values).filter(
      (key) => values[key].changed && !reviewerResolved.includes(key)
    );

    return { reviewerId: reviewer.id, changedFields: changed };
  });
}

type CompareFormPanelProps = {
  type: ExtractionFormType;
  dataExtractionForm: CompareFormProps | undefined;
  dataExtractionConsensusForm: UseFormReturn<FormValues>;
  qualityAssessmentForm: CompareFormProps | undefined;
  qualityAssessmentConsensusForm: UseFormReturn<FormValues>;
  deCompleted: boolean;
  qaCompleted: boolean;
};

const CompareFormPanel = memo(function CompareFormPanel({
  type,
  dataExtractionForm,
  dataExtractionConsensusForm,
  qualityAssessmentForm,
  qualityAssessmentConsensusForm,
  deCompleted,
  qaCompleted,
}: CompareFormPanelProps) {
  switch (type) {
    case 'data_extraction':
      if (!dataExtractionForm) return null;
      return (
        <section aria-label="Data Extraction" className={styles.tabPanel}>
          <FormProvider {...dataExtractionConsensusForm}>
            <CompareDataExtractionForm
              form={dataExtractionForm}
              completed={deCompleted}
            />
            <UnsavedChangesWarning />
          </FormProvider>
        </section>
      );
    case 'quality_assessment':
      if (!qualityAssessmentForm) return null;
      return (
        <section aria-label="Quality Assessment" className={styles.tabPanel}>
          <FormProvider {...qualityAssessmentConsensusForm}>
            <CompareQualityAssessmentForm
              form={qualityAssessmentForm}
              completed={qaCompleted}
            />
            <UnsavedChangesWarning />
          </FormProvider>
        </section>
      );
  }
});

const splitSizes: [number, number] = [40, 60];

export default function Compare({
  studyId,
  title,
  singleReviewer,
  dataExtractionForm,
  qualityAssessmentForm,
  initialSection = 'data_extraction',
  fullTexts,
  backUrl,
  editUrl,
  currentUserIsTheDecider,
  nutrientViewerKey,
}: CompareProps): JSX.Element {
  const [navigateToPreviousPage, setNavigateToPreviousPage] = useState(false);
  const [selectedTab, updateSelectedTab] = useState(initialSection);

  const dataExtractionResolutions = dataExtractionForm
    ? prepareViewModel({
        formTemplate: dataExtractionForm.template,
        reviewerExtractionData: dataExtractionForm.reviewers.map(
          (reviewer) => reviewer.currentExtraction.values
        ),
        consensusDecider: !!dataExtractionForm.consensusDecider,
        decidedValues: dataExtractionForm?.consensusDecider?.data.values,
      })
    : emptyComparisonResult;

  const qualityAssessmentResolutions = qualityAssessmentForm
    ? prepareViewModel({
        formTemplate: qualityAssessmentForm.template,
        reviewerExtractionData: qualityAssessmentForm.reviewers.map(
          (reviewer) => reviewer.currentExtraction.values
        ),
        consensusDecider: !!qualityAssessmentForm.consensusDecider,
        decidedValues: qualityAssessmentForm.consensusDecider?.data.values,
      })
    : emptyComparisonResult;

  const dataExtractionConsensusForm = useForm<FormValues>({
    defaultValues: {
      resolvedData: dataExtractionResolutions.resolvedData,
      unresolvedIDs: dataExtractionResolutions.unresolvedIDs,
      blockResolutions: dataExtractionResolutions.blockResolutions,
      unresolvedTableCells: dataExtractionResolutions.unresolvedTableCells,
    },
  });

  const qualityAssessmentConsensusForm = useForm<FormValues>({
    defaultValues: {
      resolvedData: qualityAssessmentResolutions.resolvedData,
      unresolvedIDs: qualityAssessmentResolutions.unresolvedIDs,
      blockResolutions: qualityAssessmentResolutions.blockResolutions,
      unresolvedTableCells: qualityAssessmentResolutions.unresolvedTableCells,
    },
  });

  const deCompleted =
    dataExtractionForm?.consensusDecider?.status === 'complete';
  const qaCompleted =
    qualityAssessmentForm?.consensusDecider?.status === 'complete';
  const completed = deCompleted || qaCompleted;

  useEffect(() => {
    if (navigateToPreviousPage) {
      window.location.assign(backUrl);
    }
  }, [navigateToPreviousPage, backUrl]);

  const status = (
    <UnresolvedDecisionsStatus
      isAllDecisionsResolved={completed}
      isSingleReviewer={singleReviewer}
      deControl={dataExtractionConsensusForm.control}
      qaControl={qualityAssessmentConsensusForm.control}
    />
  );

  const renderBadge = useCallback(
    (type: ExtractionFormType) => (
      <ConsensusTabBadge
        deControl={dataExtractionConsensusForm.control}
        qaControl={qualityAssessmentConsensusForm.control}
        deCompleted={deCompleted}
        qaCompleted={qaCompleted}
        type={type}
      />
    ),
    [
      dataExtractionConsensusForm.control,
      qualityAssessmentConsensusForm.control,
      deCompleted,
      qaCompleted,
    ]
  );

  const renderPanel = useCallback(
    (type: ExtractionFormType) => {
      if (type === 'data_extraction' && !dataExtractionForm) return null;
      if (type === 'quality_assessment' && !qualityAssessmentForm) return null;
      return (
        <CompareFormPanel
          type={type}
          dataExtractionForm={dataExtractionForm}
          dataExtractionConsensusForm={dataExtractionConsensusForm}
          qualityAssessmentForm={qualityAssessmentForm}
          qualityAssessmentConsensusForm={qualityAssessmentConsensusForm}
          deCompleted={deCompleted}
          qaCompleted={qaCompleted}
        />
      );
    },
    [
      dataExtractionForm,
      dataExtractionConsensusForm,
      qualityAssessmentForm,
      qualityAssessmentConsensusForm,
      deCompleted,
      qaCompleted,
    ]
  );

  return (
    <div className={styles.container}>
      <CompareHeader
        backUrl={backUrl}
        title={title}
        studyId={studyId}
        dataExtractionForm={dataExtractionForm}
        dataExtractionConsensusForm={dataExtractionConsensusForm}
        qualityAssessmentForm={qualityAssessmentForm}
        qualityAssessmentConsensusForm={qualityAssessmentConsensusForm}
        setNavigateToPreviousPage={setNavigateToPreviousPage}
        currentUserIsTheDecider={currentUserIsTheDecider}
        editUrl={editUrl}
        completed={completed}
      />
      <QueryProvider>
        <DocumentViewerProvider licenseKey={nutrientViewerKey}>
          <Split className={styles.split} sizes={splitSizes} gutterSize={5}>
            <div className={styles.previewPanel}>
              <FullTextsPreview documents={fullTexts} />
            </div>
            <section
              id={'consensus-root-id'}
              className={styles.forms}
              aria-label="Consensus form"
            >
              <header className={styles.consensusHeader}>
                <h4 className={styles.consensusHeading}>
                  {singleReviewer ? 'Check data' : 'Consensus'}
                </h4>
                {status}
              </header>

              <ExtractionTabs
                selected={selectedTab}
                onSelect={updateSelectedTab}
                renderBadge={renderBadge}
                renderPanel={renderPanel}
              />
            </section>
          </Split>
        </DocumentViewerProvider>
      </QueryProvider>
    </div>
  );
}
